import React, { Fragment, useEffect, useMemo, useState } from "react";
import "src/index.css";
import "moment-timezone";
import moment from "moment/moment";
import AuthService from "src/service/api";
import { useNavigate } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import PaymentIcon from "src/images/payments.png";
import { capitalizedWord } from "src/helper/global";
import Header from "src/components/Header/Header";
import OrderHeader from "src/components/Order/OrderHeader";
import OrderProduct from "src/components/Order/OrderProduct";
import OrderCharges from "src/components/Order/OrderCharges";
import { groupByCategory } from "src/helper/groupByCategory";
import { createItemPromotionLookup } from "src/helper/itemPromotions";
import CustomLoader from "src/components/Preloader/Preloader";
import OrderDetails from "src/components/Order/OrderDetails/OrderDetails";

export const Order = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [orderData, setOrderData] = useState({});
  const [timeZoneId, setTimeZoneId] = useState("America/Los_Angeles");
  const [marketplaceConfig, setMarketplaceConfig] = useState({
    logo: '',
    name: '',
    height: '',
    width: ''
  });
  const [isItemsOpen, setIsItemsOpen] = useState(false);
  const productsData = groupByCategory(orderData?.products);
  const itemPromotions = useMemo(
    () => createItemPromotionLookup(orderData),
    [orderData]
  );

  useEffect(() => {
    let parsedParams = (url) => {
      return url.split("/");
    };

    const params = parsedParams(window.location.pathname);
    const businessId = params[1];
    const orderId = params[2];

    if (!businessId || !orderId) {
      navigate("/404");
    }

    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await AuthService.getOrderData(businessId, orderId);
        if (response?.data?.data?.business) {
          document.title = `Orders co | ${response.data.data.business[0].companyName}`;
        } else {
          document.title = `Orders co`;
        }
        if (response) {
          setMarketplaceConfig({
            name: response.data?.data?.orderMetaData?.marketplaceName,
            logo: response.data?.data?.orderMetaData?.marketplaceLogo,
            height: response.data?.data?.orderMetaData?.marketplaceLogoHeight || '37px',
            width: response.data?.data?.orderMetaData?.marketplaceLogoWidth || '152px',
          })
          setOrderData(response.data.data || {});
          setTimeZoneId(
            response?.data?.data?.business[0]?.timezone?.timeZoneId
          );
          setTimeout(() => {
            setLoading(false);
          },1500)
        }
      } catch (e) {
        document.title = `Orders co`;
        setOrderData({});
        setTimeZoneId("America/Los_Angeles");
        setTimeout(() => {
          setLoading(false);
        },1500)
      }
    };
    fetchData().then();
  }, []);

  const convertPayment = (method) => {
    const PAYMENTS = {
      google_pay: "Google Pay",
      apple_pay: "Apple Pay",
      cash: "Cash",
      card: "Card",
      credit: "External Card",
      reward: "Reward",
      coupon: "Coupon",
      link: "Link",
    };
    return PAYMENTS[method];
  };

  if (loading) {
    return  <CustomLoader />
  }

  return (
    <>
      <Header marketplaceConfig={marketplaceConfig}  />
      <main>
        {orderData && Object.keys(orderData)?.length > 0 && (
          <Box id="receipt">
            <OrderHeader orderData={orderData} marketplaceConfig={marketplaceConfig} />
            <OrderDetails orderData={orderData} timeZoneId={timeZoneId} />

            <Box className="accordion">
              <Box
                className="accordion-header"
                aria-expanded={isItemsOpen}
                onClick={() => setIsItemsOpen(!isItemsOpen)}
              >
                <Typography className="items">Items: (click to expand/collapse)</Typography>
                <span>{isItemsOpen ? "-" : "+"}</span>
              </Box>
              <Box className={`items-container ${isItemsOpen ? "open" : ""}`}>
                {isItemsOpen && (
                  <Box id="table">
                    <Box>
                      <Typography className="items">Items:</Typography>
                    </Box>
                    {Object.keys(productsData)?.length > 0 ? (
                      Object.keys(productsData)?.map((keys, index) => {
                        return (
                          <>
                            <Fragment key={`${keys}_${index}`}>
                              {productsData[keys]?.categoryName ? (
                                <>
                                  {index !== 0 ? (
                                    <Box
                                      style={{
                                        marginBottom: "16px",
                                        marginTop: "16px",
                                        borderTop: "1px solid rgba(0, 0, 0, 0.4)",
                                      }}
                                    ></Box>
                                  ) : (
                                    <></>
                                  )}
                                  <Box
                                    style={{
                                      marginBottom: "3px",
                                    }}
                                  >
                                    <Typography
                                      className="product"
                                    >
                                      {productsData[
                                        keys
                                        ]?.categoryName?.toUpperCase()}
                                    </Typography>
                                    <Box
                                      className="product  product-longer-price"
                                      style={{
                                        width: "100%",
                                      }}
                                    ></Box>
                                  </Box>
                                </>
                              ) : (
                                <></>
                              )}
                            </Fragment>
                            {productsData[keys]?.items?.map((product, index) => {
                              return (
                                <Fragment key={`${product?._id}_${index}`}>
                                  <OrderProduct
                                    product={product}
                                    promotionLines={itemPromotions.linesFor(product)}
                                  />
                                </Fragment>
                              );
                            })}
                          </>
                        );
                      })
                    ) : (
                      <></>
                    )}
                  </Box>
                )}
              </Box>
            </Box>
            <hr className="line-header" />
            <table id="table">
              <tbody>
               <OrderCharges
                 orderData={orderData}
                 itemPromotions={itemPromotions}
               />
              </tbody>
            </table>
            {orderData?.payment?.payment_method_details ? (
              <>
                <hr className="line-header" />
                <Box className="payment-container">
                  <Box>
                    <img src={PaymentIcon} alt="payment icon" />
                  </Box>
                  <Box>
                    {orderData?.payment?.payment_method_details?.card?.wallet
                      ?.type ? (
                      <Typography className="payment-title">
                        Payments: by{" "}
                        {convertPayment(
                          orderData?.payment?.payment_method_details?.card?.wallet
                            ?.type
                        )}
                      </Typography>
                    ) : orderData?.payment?.payment_method_details?.card?.last4 ||
                    orderData?.payment?.payment_method_details?.card?.brand ? (
                      <Typography className="payment-title">
                        Payments: by {convertPayment("card")}
                      </Typography>
                    ) : (
                      <></>
                    )}
                    {orderData?.payment?.payment_method_details?.card?.brand ? (
                      <Typography className="cart-number">
                        Card Type:{" "}
                        {`${capitalizedWord(
                          orderData?.payment?.payment_method_details?.card
                            ?.brand || ""
                        )}`}
                      </Typography>
                    ) : (
                      <></>
                    )}
                    {orderData?.payment?.payment_method_details?.card?.last4 ? (
                      <Typography className="cart-number">
                        Card no:{" "}
                        {`XXXX XXXX XXXX ${orderData?.payment?.payment_method_details?.card?.last4}`}
                      </Typography>
                    ) : (
                      <></>
                    )}
                    <Typography className="cart-number-title">
                      Date:{" "}
                      {moment(orderData?.formattedCreatedAt).format(
                        "hh:mm A - ddd, MMM Do"
                      )}
                    </Typography>
                  </Box>
                </Box>
              </>
            ) : (
              <></>
            )}
          </Box>
        )}
      </main>
    </>
  );
};
